import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { StepContainer } from '../../components/StepContainer/StepContainer';
import { StepFooter } from '../../components/StepFooter/StepFooter';
import { useCharitableGivingContext } from '../../context/useCharitableGivingContext';
import { FormValues, STEP_ID, validation } from './GivingStrategy.meta';
import { Select } from '../../../../../CommonComponents/OPL/Select/Select/Select';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { INPUTS_WIDTH, STEPS_DICT } from '../../CharitableGiving.meta';
import { useNavigation } from '../../hooks/useNavigation';
import { WorkflowDTO } from '../../api/useCharitableGivingQuery';

export const GivingStrategy = () => {
  const { workflow, setWorkflow, setStep, setSteps } = useCharitableGivingContext();

  const { progress, previousStep, nextStep, isFirstStep } = useNavigation({
    currentStep: STEP_ID,
  });

  const { control, handleSubmit, formState } = useForm<FormValues>({
    defaultValues: {
      givingStrategy: workflow.data.givingStrategy,
    },
    resolver: yupResolver(validation),
  });

  const onSubmit = handleSubmit(values => {
    const newWorkflow: WorkflowDTO = {
      ...workflow,
      data: {
        ...workflow.data,
        givingStrategy: values.givingStrategy,
      },
    };

    setWorkflow(newWorkflow);

    if (nextStep) {
      setStep(nextStep);
    }
  });

  const { givingStrategyOptions } = workflow.options;

  return (
    <StepContainer
      title="Which giving strategy would you like to choose?"
      onSubmit={onSubmit}
      footer={
        <StepFooter
          isFirstStep={isFirstStep}
          progress={progress}
          onPrevious={previousStep ? () => setStep(previousStep) : undefined}
        />
      }
    >
      <Controller
        control={control}
        name="givingStrategy"
        render={({ field: { value: formValue, onChange, ref } }) => (
          <Box style={{ width: INPUTS_WIDTH }}>
            <Select
              innerRef={ref}
              label="Giving Strategy"
              value={givingStrategyOptions.find(({ value }) => value === formValue)}
              onChange={opt => {
                // Set the next step
                if (opt) {
                  setSteps(STEPS_DICT[opt.value]);
                }

                // Reset selected years when changing strategy, as available year options may vary.
                const newWorkflow: WorkflowDTO = {
                  ...workflow,
                  data: {
                    ...workflow.data,
                    donationStartYear: null,
                    donationEndYear: null,
                    dafDonationStartYear: null,
                    dafDonationEndYear: null,
                    dafGrantStartYear: null,
                    dafGrantEndYear: null,
                  },
                };

                setWorkflow(newWorkflow);

                onChange(opt?.value);
              }}
              options={givingStrategyOptions}
              formVariant={formState.errors.givingStrategy ? 'error' : undefined}
              helperText={formState.errors.givingStrategy?.message}
            />
          </Box>
        )}
      />
    </StepContainer>
  );
};
